<script setup lang="ts">
import { useToast } from '@/shared/composables/useToast';
import { cn } from '@/shared/utils/cn';
import { CircleAlert, CircleCheck, Info, TriangleAlert, X } from '@lucide/vue';

const { toasts, dismiss } = useToast();

function iconFor(kind: string) {
  switch (kind) {
    case 'success':
      return CircleCheck;
    case 'warning':
      return TriangleAlert;
    case 'error':
      return CircleAlert;
    default:
      return Info;
  }
}

function paletteFor(kind: string): string {
  switch (kind) {
    case 'success':
      return 'border-success/40 bg-success-soft text-foreground';
    case 'warning':
      return 'border-warning/50 bg-warning-soft text-foreground';
    case 'error':
      return 'border-destructive/50 bg-destructive-soft text-foreground';
    default:
      return 'border-info/40 bg-info-soft text-foreground';
  }
}

function accentFor(kind: string): string {
  switch (kind) {
    case 'success':
      return 'text-success';
    case 'warning':
      return 'text-warning';
    case 'error':
      return 'text-destructive';
    default:
      return 'text-info';
  }
}
</script>

<template>
  <Teleport to="body">
    <div class="toast-host pointer-events-none fixed top-4 end-4 z-[200] flex w-full max-w-sm flex-col gap-2">
      <transition-group name="toast">
        <div
          v-for="item in toasts"
          :key="item.id"
          :class="cn('pointer-events-auto flex items-start gap-3 rounded-lg border p-3 shadow-lg', paletteFor(item.tone))"
          role="status"
          :aria-live="item.tone === 'error' ? 'assertive' : 'polite'"
        >
          <component :is="iconFor(item.tone)" :class="cn('mt-0.5 h-5 w-5 shrink-0', accentFor(item.tone))" />
          <div class="min-w-0 flex-1 text-sm">
            <p class="font-medium">{{ item.title }}</p>
            <ul v-if="item.messages.length" class="mt-1 space-y-0.5">
              <li v-for="(line, index) in item.messages" :key="index">{{ line }}</li>
            </ul>
          </div>
          <button
            type="button"
            class="shrink-0 rounded p-1 text-muted-foreground hover:bg-muted hover:text-foreground"
            aria-label="Close"
            @click="dismiss(item.id)"
          >
            <X class="h-4 w-4" />
          </button>
        </div>
      </transition-group>
    </div>
  </Teleport>
</template>

<style scoped>
.toast-enter-active,
.toast-leave-active {
  transition: opacity 0.18s ease, transform 0.18s ease;
}
.toast-enter-from,
.toast-leave-to {
  opacity: 0;
  transform: translateY(-8px);
}
</style>
